import { useState, useEffect, useCallback } from 'react';
import { 
  serviceProviderService, 
  PendingRegistration, 
  PendingRegistrationsFilters,
  ServiceProviderType 
} from '../services/serviceProviderService';

interface UsePendingRegistrationsReturn {
  pendingRegistrations: PendingRegistration[];
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  } | null;
  serviceProviderTypes: ServiceProviderType[];
  serviceProviderTypeFilter: string;
  fetchPendingRegistrations: (filters?: PendingRegistrationsFilters) => Promise<void>;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (limit: number) => void;
  setServiceProviderTypeFilter: (type: string) => void;
  refresh: () => Promise<void>;
}

export const usePendingRegistrations = (): UsePendingRegistrationsReturn => {
  const [pendingRegistrations, setPendingRegistrations] = useState<PendingRegistration[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<{
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  } | null>(null);
  const [serviceProviderTypes, setServiceProviderTypes] = useState<ServiceProviderType[]>([]);
  
  // Filter states
  const [currentPage, setCurrentPageState] = useState<number>(1);
  const [itemsPerPage, setItemsPerPageState] = useState<number>(10);
  const [serviceProviderTypeFilter, setServiceProviderTypeFilterState] = useState<string>('');

  const fetchPendingRegistrations = useCallback(async (filters: PendingRegistrationsFilters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      
      const response = await serviceProviderService.getPendingRegistrations({
        page: currentPage,
        limit: itemsPerPage,
        serviceProviderType: serviceProviderTypeFilter || undefined,
        ...filters
      });
      
      if (response.success) {
        setPendingRegistrations(response.data.serviceProviders || []);
        setPagination(response.data.pagination);
        
        // Extract unique service provider types for filter dropdown
        const uniqueTypes = (response.data.serviceProviders || []).reduce((acc: ServiceProviderType[], registration) => {
          const spType = registration.serviceProviderType;
          if (!spType?.tag || !spType?.name) return acc;
          const existingType = acc.find(type => type.tag === spType.tag);
          if (!existingType) {
            acc.push(spType);
          }
          return acc;
        }, []);
        setServiceProviderTypes(uniqueTypes);
        
      } else {
        throw new Error('Failed to fetch pending registrations');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      console.error('❌ Error fetching pending registrations:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, serviceProviderTypeFilter]);

  const setCurrentPage = useCallback((page: number) => {
    setCurrentPageState(page);
  }, []);

  const setItemsPerPage = useCallback((limit: number) => {
    setItemsPerPageState(limit);
    setCurrentPageState(1); // Reset to first page when changing items per page
  }, []);

  const setServiceProviderTypeFilter = useCallback((type: string) => {
    setServiceProviderTypeFilterState(type);
    setCurrentPageState(1); // Reset to first page when changing filter
  }, []);

  const refresh = useCallback(async () => {
    await fetchPendingRegistrations();
  }, [fetchPendingRegistrations]);

  // Fetch data when filters change
  useEffect(() => {
    fetchPendingRegistrations();
  }, [fetchPendingRegistrations]);

  return {
    pendingRegistrations,
    loading,
    error,
    pagination,
    serviceProviderTypes,
    serviceProviderTypeFilter,
    fetchPendingRegistrations,
    setCurrentPage,
    setItemsPerPage,
    setServiceProviderTypeFilter,
    refresh
  };
};